import { Button, NookLogo } from "@nook/ui"
import { Link, useSearch } from "@tanstack/react-router"
import { Loader2 } from "lucide-react"
import { useState } from "react"

import { supabase } from "@/lib/supabase.js"

/** Same-origin paths only; anything else is an open redirect and goes to `/`. */
export const safeRedirect = (path: unknown) =>
  typeof path === "string" &&
  path.startsWith("/") &&
  new URL(path, window.location.origin).origin === window.location.origin
    ? path
    : "/"

/** The marketing site: the logo's way out, and the legal pages (block 35). */
const SITE_URL = "https://www.getnook.tech"

const copy = {
  signin: {
    heading: "Sign in to Nook",
    tagline: "Nook anything. Ask it later, in your own words.",
    verb: "Sign in",
    prompt: "New to Nook?",
    other: { to: "/signup", label: "Sign up" },
  },
  signup: {
    heading: "Create your account",
    tagline:
      "Nook is invite-only for now. Sign up with the Google or Apple account your invite went to.",
    verb: "Sign up",
    prompt: "Already have an account?",
    other: { to: "/signin", label: "Sign in" },
  },
} as const

/** Google, then Apple (iOS block 01, I6). Apple's glyph is black, inverted in dark. */
const providers = [
  { id: "google", label: "Google", icon: "/google.svg", className: "" },
  { id: "apple", label: "Apple", icon: "/apple.svg", className: "dark:invert" },
] as const

/**
 * The one sign-in surface for every client (block 01). `/signup` is the same
 * OAuth flow with sign-up copy: OAuth makes no difference between the two.
 *
 * The extension does not run its own OAuth flow: its popup opens `/signin` in a
 * tab and picks the resulting session up from the broadcast in `main.tsx`. One
 * OAuth client, one consent screen, one redirect allow-list to keep correct —
 * and `chrome.identity.launchWebAuthFlow`, which the spec originally called
 * for, disappears entirely.
 *
 * A dedicated route rather than a button on `/` because the extension needs a
 * URL that stays valid whatever block 02 does to the home screen.
 *
 * From block 02 it is also **the** signed-out surface of the webapp: `/` is
 * guarded and redirects here. There is deliberately no placeholder landing —
 * block 40 sends signed-out visitors to the marketing site on the apex, so a
 * landing built here would be replaced before anyone read it.
 *
 * Signups are disabled until launch, so an uninvited Google account comes back
 * with `?error_code=signup_disabled` (plan/README.md §4). `/signup` says so up
 * front rather than promising an account it cannot create.
 */
export function AuthPage({ mode }: { mode: keyof typeof copy }) {
  const { redirect: to, error_code, error_description } = useSearch({ from: "/_auth" })
  const { heading, tagline, verb, prompt, other } = copy[mode]
  // The provider's page replaces this one; until it does, the button says it was pressed.
  const [leaving, setLeaving] = useState<string | null>(null)
  const error =
    {
      signup_disabled: "Nook is invite-only for now.",
      account_deleted:
        "Your account is deleted. To restore it, email support@getnook.tech within 60 days of deleting it.",
    }[error_code ?? ""] ?? error_description

  // Laid out after Microsoft Copilot's sign-in, mirrored: the sky is the
  // landing page's, still, and hidden below `lg`.
  return (
    <main className="grid min-h-dvh lg:grid-cols-2">
      <section className="hidden items-end justify-start bg-[url(/sky.webp)] bg-cover bg-left p-10 lg:flex">
        {/* The marketing site's glass, so the note reads as part of the same sky. */}
        {mode === "signup" && (
          <figure className="max-w-sm space-y-5 rounded-[12px] border border-white/25 bg-white/15 p-6 text-left text-white backdrop-blur-xl animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-500 ease-out">
            <blockquote className="space-y-2 text-[15px] leading-relaxed text-pretty">
              <p>
                I built Nook because the good things I found online kept getting lost in tabs and
                folders I never opened again.
              </p>
              <p>Save it in a click. Ask for it later, in your own words.</p>
            </blockquote>
            <figcaption className="flex flex-col items-start gap-2">
              <p className="text-[15px]">Keep it simple,</p>
              <img src="/signature.svg" alt="Mainak" className="h-14 w-auto" />
              <p className="text-xs font-medium text-white/80">Mainak, Founder</p>
            </figcaption>
          </figure>
        )}
      </section>
      <section className="relative flex flex-col items-center justify-center gap-6 p-6 text-center">
        <a href={SITE_URL} aria-label="Nook home">
          <NookLogo className="size-12" />
        </a>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold tracking-tight">{heading}</h1>
          <p className="max-w-xs text-sm text-muted-foreground">{tagline}</p>
        </div>
        <div className="flex flex-col gap-3">
          {providers.map(({ id, label, icon, className }) => (
            <Button
              key={id}
              variant="outline"
              size="lg"
              className="rounded-full shadow-sm"
              disabled={!!leaving}
              onClick={() => {
                setLeaving(id)
                void supabase.auth.signInWithOAuth({
                  provider: id,
                  // Back to this page, so a rejection can be shown. A success
                  // never renders it: `beforeLoad` sends the session on to `redirect`.
                  options: {
                    redirectTo: `${window.location.origin}/${mode}?redirect=${encodeURIComponent(safeRedirect(to))}`,
                  },
                })
              }}
            >
              {leaving === id ? <Loader2 className="size-4 animate-spin" /> : <img src={icon} alt="" className={`size-4 ${className}`} />}
              {verb} with {label}
            </Button>
          ))}
        </div>
        {error && (
          <p role="alert" className="border-destructive/30 bg-destructive/10 text-destructive max-w-xs rounded-md border px-3 py-2 text-sm">
            {error}
          </p>
        )}
        <p className="text-sm text-muted-foreground">
          {prompt}{" "}
          <Link
            to={other.to}
            search={to ? { redirect: to } : {}}
            className="font-medium text-foreground underline-offset-4 hover:underline"
          >
            {other.label}
          </Link>
        </p>
        <footer className="absolute bottom-6 flex gap-4 text-xs text-muted-foreground">
          {[["terms", "Terms of Service"], ["privacy", "Privacy Policy"]].map(([path, label]) => (
            <a key={path} href={`${SITE_URL}/${path}`} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
              {label}
            </a>
          ))}
        </footer>
      </section>
    </main>
  )
}
